/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';
import {
	Jumbo,
	Separator,
	DateIntervalNavigation,
	createTimeMachine,
	Container,
	Card,
	Subheading,
	Title,
	ITimeMachine,
	Paragraph,
	DateIntervalValue,
	createTimeMachineFromQuery,
	Heading,
	createCustomTimeMachine,
} from '@treams/treams-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useRouteMatch } from 'react-router';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import moment from 'moment';

import * as graphql from 'generated/graphql';
import { camelCase, upperFirst } from 'lodash';
import Breadcrumbs from 'goals/group-selector/Breadcrumbs';
import { AnalyticsMetricContainer, valueFromMetric } from './metrics/AnalyticsMetric';
import useWorkspaceConfiguration from 'packages/kakapo-ui-application/hooks/useWorkspaceConfiguration';
import { displayMetric } from './metrics/AnalyticsNumberMetric';
import { TrafficLight } from './metrics/TrafficLight';
import AnalyticsMetricDetails from './metric/AnalyticsMetricDetails';
import EngagementAnalytics from './EngagementAnalytics';
import GroupSelector from 'goals/group-selector/GroupSelector';

const PageBackground = styled(Flex)`
	background-color: ${(props) => props.theme.colors.whiteBlue};
	flex-grow: 1;
	flex-direction: column;
	width: 100%;
`;

const Grid = styled(Box)`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	gap: 20px 12px;
`;

function hasThreshold(metric: graphql.AnalyticsMetric) {
	return (
		metric.__typename === 'AnalyticsIntegerMetric' ||
		metric.__typename === 'AnalyticsFloatMetric' ||
		metric.__typename === 'AnalyticsPercentageMetric' ||
		metric.__typename === 'AnalyticsFractionMetric'
	);
}

function hasDetails(
	metric: graphql.AnalyticsMetric,
): metric is
	| graphql.AnalyticsIntegerMetric
	| graphql.AnalyticsFloatMetric
	| graphql.AnalyticsPercentageMetric
	| graphql.AnalyticsFractionMetric {
	return (
		metric.__typename === 'AnalyticsIntegerMetric' ||
		metric.__typename === 'AnalyticsFloatMetric' ||
		metric.__typename === 'AnalyticsPercentageMetric' ||
		metric.__typename === 'AnalyticsFractionMetric'
	);
}

function MetricValue(props: { variables: any; timeMachine: ITimeMachine; previous?: boolean }) {
	const { t } = useTranslation();

	const { data } = graphql.useAnalyticsMetricQuery({
		variables: {
			...props.variables,
			filters: {
				startDate: props.timeMachine.current[0],
				endDate: props.timeMachine.current[1],
			},
		},
		fetchPolicy: 'no-cache',
	});

	const metric = data ? data.analytics.metricGroup.metric : undefined;

	return (
		<>
			<Flex alignItems="center">
				<Subheading>
					<DateIntervalValue value={props.timeMachine} />
				</Subheading>
				{data && metric && hasThreshold(metric as graphql.AnalyticsMetric) ? (
					<TrafficLight
						value={valueFromMetric(data?.analytics.metricGroup.metric)}
						totalUsers={
							metric.__typename === 'AnalyticsIntegerMetric'
								? data.analytics.totalUsers
								: metric.__typename === 'AnalyticsFractionMetric'
								? metric.total
								: undefined
						}
						threshold={
							(
								data.analytics.metricGroup.metric as
									| graphql.AnalyticsIntegerMetric
									| graphql.AnalyticsFloatMetric
									| graphql.AnalyticsPercentageMetric
									| graphql.AnalyticsFractionMetric
							).threshold
						}
					/>
				) : (
					<TrafficLight.Skeleton ml={2} />
				)}
			</Flex>
			<Paragraph mb={3}>
				{t(`analytics.metric.period.${props.previous ? 'previous' : 'current'}`)}
			</Paragraph>

			{data?.analytics.metricGroup.metric ? (
				<Title>
					{displayMetric(
						data.analytics.metricGroup.metric as
							| graphql.AnalyticsIntegerMetric
							| graphql.AnalyticsFloatMetric
							| graphql.AnalyticsPercentageMetric
							| graphql.AnalyticsFractionMetric
							| graphql.AnalyticsConversationTemplateMetric,
						valueFromMetric,
					)}
				</Title>
			) : (
				<Title.Skeleton />
			)}
		</>
	);
}

function ThresholdTitle(props: { metric: graphql.AnalyticsMetric; signal: 'good' | 'bad' }) {
	const { t } = useTranslation();

	const translationKey = (() => {
		if (props.metric.id === 'engagement.average') {
			return 'engagement.score';
		}

		if (
			props.metric.__typename === 'AnalyticsIntegerMetric' ||
			props.metric.__typename === 'AnalyticsFloatMetric'
		) {
			return 'averagePerEmployee';
		}

		if (props.metric.__typename === 'AnalyticsPercentageMetric') {
			return 'percentage';
		}

		if (props.metric.__typename === 'AnalyticsFractionMetric') {
			return 'fraction';
		}

		return 'Missing key';
	})();

	return (
		<>
			{t(`analytics.metric.kpi.expectation.${props.signal}.${translationKey}`, {
				value: displayMetric(props.metric as any, (metric) => {
					if (metric.__typename === 'AnalyticsFractionMetric') {
						return (metric.threshold * metric.total).toFixed(0);
					}
					return metric.threshold;
				}),
			})}
		</>
	);
}

function Threshold(props: { metric?: graphql.AnalyticsMetric; signal: 'good' | 'bad' }) {
	return (
		<AnalyticsMetricContainer py={2}>
			<Flex alignItems="center">
				{!props.metric ? (
					<Paragraph.Skeleton lines={1} />
				) : (
					<>
						<Flex alignItems="center">
							<TrafficLight.Element signal={props.signal} mr={2} />
						</Flex>

						{props.metric && (
							<Paragraph>
								<ThresholdTitle metric={props.metric} signal={props.signal} />
							</Paragraph>
						)}
					</>
				)}
			</Flex>
		</AnalyticsMetricContainer>
	);
}

export default function Metric() {
	const { t } = useTranslation();
	const location = useLocation();
	const configuration = useWorkspaceConfiguration();

	const [timeMachine, setTimeMachine] = useState<ITimeMachine>(createTimeMachineFromQuery());

	const { params } = useRouteMatch<{ metricGroupID: string; metricID: string }>();

	const sharedVariables = {
		type: graphql.AnalyticsType.Workspace,
		metricGroupID: params.metricGroupID,
		metricID: `${params.metricGroupID}.${camelCase(params.metricID)}`,
	};

	const [groups, setGroups] = useState<graphql.Group[]>([]);

	const filters = {
		startDate: timeMachine.current[0],
		endDate: timeMachine.current[1],
		groups: groups.map((group) => _.pick(group, ['id', 'type'])),
	};

	const { data } = graphql.useAnalyticsMetricQuery({
		variables: {
			...sharedVariables,
			filters,
		},
	});

	const previousTimeMachine = timeMachine.previous
		? timeMachine.interval === 'custom'
			? createCustomTimeMachine(timeMachine.previous, timeMachine.current[0])
			: createTimeMachine(timeMachine.interval, timeMachine.previous)
		: undefined;

	const descriptionKey = `analytics.metric.description.${data?.analytics.metricGroup.metric.id}`;

	return (
		<>
			<Container mt={4}>
				{!data ? (
					<Breadcrumbs.Skeleton />
				) : (
					<Breadcrumbs
						components={[
							{
								id: 'analytics',
								name: t('analytics.breadcrumb.analytics'),
								to: `/analytics/v2${location.search}`,
							},
							{
								id: 'group',
								name: t(`analytics.metrics.groupTitle.${params.metricGroupID}`),
								readOnly: true,
							},
							{
								id: 'metric',
								name: upperFirst(t(`analytics.metrics.${data.analytics.metricGroup.metric.id}`)),
								readOnly: true,
							},
						]}
					/>
				)}
			</Container>
			<Jumbo
				title={
					data?.analytics.metricGroup.metric.id
						? upperFirst(t(`analytics.metrics.${data.analytics.metricGroup.metric.id}`))
						: undefined
				}
				footer={<Separator />}
			>
				<Flex alignItems="center" mt={3}>
					<Paragraph flexGrow={0} flexShrink={0} mr={2}>
						{t('dashboard.sidebar.filters.departments.title')}:
					</Paragraph>
					<Box mb="-8px">
						<GroupSelector
							canSelect={(group) =>
								group.viewerCanViewAnalytics && group.type === graphql.GroupType.Department
							}
							onChange={setGroups}
							initialGroups={[]}
							remember="analyticsGroupIDs"
						/>
					</Box>
					<Box flexGrow={1} flexShrink={1} />
					<DateIntervalNavigation
						value={timeMachine}
						onChange={(newValue) => {
							setTimeMachine(newValue);
						}}
						workspaceStartDate={
							configuration.data?.startDate
								? moment(configuration.data.startDate).toDate()
								: undefined
						}
					/>
				</Flex>
			</Jumbo>
			<PageBackground>
				<Container py={4}>
					<Box mb={4}>
						<Heading> {t('analytics.metric.description.title')}</Heading>
						{!data ? (
							<Paragraph.Skeleton lines={2} />
						) : (
							<Paragraph>
								{t(descriptionKey) !== descriptionKey
									? t(descriptionKey)
									: t('analytics.metric.description.empty')}
							</Paragraph>
						)}
					</Box>
					<Grid>
						<Card>
							{previousTimeMachine && (
								<MetricValue
									variables={sharedVariables}
									timeMachine={previousTimeMachine}
									previous
								/>
							)}
						</Card>

						<Card>
							<MetricValue variables={sharedVariables} timeMachine={timeMachine} />
						</Card>

						<Box px={3}>
							<Subheading mb={2} mt={3}>
								{t(`analytics.metric.kpis`)}
							</Subheading>

							<Threshold
								metric={data?.analytics.metricGroup.metric as graphql.AnalyticsMetric}
								signal="good"
							/>
							<Threshold
								metric={data?.analytics.metricGroup.metric as graphql.AnalyticsMetric}
								signal="bad"
							/>
						</Box>
					</Grid>

					{data?.analytics.metricGroup.metric &&
						hasDetails(data.analytics.metricGroup.metric as graphql.AnalyticsMetric) &&
						(data.analytics.metricGroup.metric as any).details.length !== 0 && (
							<AnalyticsMetricDetails
								ids={(data.analytics.metricGroup.metric as any).details}
								type={sharedVariables.type}
								filters={filters}
								timeMachine={timeMachine}
							/>
						)}

					{data?.analytics.metricGroup.metric.id.startsWith('engagement.') && (
						<Box mt={4}>
							<EngagementAnalytics
								eNPS={data?.analytics.metricGroup.metric.id === 'engagement.enps'}
								filters={{
									startDate: timeMachine.current[0],
									endDate: timeMachine.current[1],
									groupIDs: groups.map(({ id }) => id),
								}}
							/>
						</Box>
					)}
				</Container>
			</PageBackground>
		</>
	);
}
